import type { StructuredMultiSearchRequest } from '@massilia/queries'

import { getMaxSearchDate, isOutsideSearchWindow, useAppForm } from '@massilia/features'
import { Button, useIsMobile } from '@massilia/ui'
import { format, min, parse } from 'date-fns'
import { Plus, Search } from 'lucide-react'

import type { FlightSearchSchemaType } from '@/lib/schemas'

import {
  DEFAULT_FLIGHT_LET,
  DEFAULT_OPTION,
  travelClassTypesLabels,
} from '@/lib/schemas'

import { FlightLegRowFormGroup } from '../research-bar/-form-groups/flight-leg-row-form-group'
import { PassengersFormGroup } from '../research-bar/-form-groups/passengers-form-group'

const FIELD_LABEL = 'text-gray-light-600 text-[11px] font-semibold tracking-wide uppercase'

interface MultiFlightSearchFormProps {
  isLoading?: boolean
  onSubmit: (params: StructuredMultiSearchRequest) => void
  searchParams: StructuredMultiSearchRequest
}

export function MultiFlightSearchForm({
  isLoading = false,
  onSubmit,
  searchParams,
}: MultiFlightSearchFormProps) {
  const isMobile = useIsMobile()

  const form = useAppForm({
    defaultValues: structuredToFormValues(searchParams),
    onSubmit: ({ value }) => {
      onSubmit(formValuesToStructured(value))
    },
  })

  const pushFlight = () => {
    const lastFlight = form.state.values.flight.at(-1)
    form.pushFieldValue('flight', {
      ...DEFAULT_FLIGHT_LET,
      date: {
        from: min([lastFlight?.date.to ?? lastFlight?.date.from ?? new Date(), getMaxSearchDate()]),
      },
      departure: lastFlight?.destination ?? DEFAULT_OPTION,
    } as FlightSearchSchemaType['flight'][number])
  }

  const legsArray = (
    <form.AppField mode="array" name="flight">
      {(field) => (
        <>
          {field.state.value.map((_, i) => {
            const removable = field.state.value.length > 1
            return (
              <FlightLegRowFormGroup
                dateType="range"
                disabledDateFn={(date) => {
                  const previousDate = field.form.getFieldValue(`flight[${i - 1}].date.from`) as Date | undefined
                  return (previousDate !== undefined && date < previousDate) || isOutsideSearchWindow(date)
                }}
                errorsDisplayType="bellow"
                fields={{
                  date: `flight[${i}].date`,
                  departure: `flight[${i}].departure`,
                  destination: `flight[${i}].destination`,
                }}
                form={form}
                key={i}
                multiLeg
                onRemove={
                  removable
                    ? () => { field.removeValue(i) }
                    : undefined
                }
              />
            )
          })}
        </>
      )}
    </form.AppField>
  )

  const passengersField = (
    <form.AppField name="passengers">
      {() => (
        <PassengersFormGroup
          fields={{
            adults: 'passengers.adults',
            babies: 'passengers.babies',
            infants: 'passengers.infants',
            seniors: 'passengers.seniors',
            youngsters: 'passengers.youngsters',
          }}
          form={form}
        />
      )}
    </form.AppField>
  )

  const travelClassField = (
    <form.AppField name="travelClass">
      {(field) => (
        <field.SelectField
          options={Object.entries(travelClassTypesLabels).map(([key, value]) => ({
            label: value,
            value: key,
          }))}
          overlayOnMobile
        />
      )}
    </form.AppField>
  )

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        void form.handleSubmit()
      }}
    >
      {isMobile ? (
        <div className="flex flex-1 flex-col gap-4">
          {legsArray}

          <Button
            className="w-full justify-center"
            onClick={pushFlight}
            type="button"
            variant="outline"
          >
            <Plus className="h-5 w-5" />
            <span className="text-sm font-medium">Ajouter un vol</span>
          </Button>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1 [&>button]:h-14 [&>button]:w-full">
              <span className={FIELD_LABEL}>Voyageurs</span>
              {passengersField}
            </div>
            <div className="flex flex-col gap-1 [&>div>button]:h-14">
              <span className={FIELD_LABEL}>Classe</span>
              {travelClassField}
            </div>
          </div>

          <div className="border-input flex items-center rounded-xl border px-4 py-3">
            <form.AppField name="withLuggage">
              {(field) => (
                <field.SwitchField
                  containerClassName="w-full items-center"
                  label="Bagages en soute inclus"
                />
              )}
            </form.AppField>
          </div>

          <Button
            className="bg-brand-600 hover:bg-brand-800 h-12 w-full justify-center text-base font-semibold text-white"
            disabled={isLoading}
            type="submit"
          >
            <Search className="size-4" />
            Rechercher
          </Button>
        </div>
      ) : (
        <div className="flex flex-1 flex-col gap-4">
          <div className="flex flex-row flex-wrap items-center gap-x-4 gap-y-3">
            {passengersField}
            {travelClassField}
          </div>

          <div className="flex flex-col flex-wrap items-center gap-3">
            {legsArray}
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <Button onClick={pushFlight} type="button">
                <Plus className="h-5 w-5" />
                <p className="text-sm font-medium">Ajouter un vol</p>
              </Button>

              <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-4">
                <form.AppField
                  children={(field) => (
                    <field.SwitchField
                      containerClassName="flex-row-reverse items-center gap-2"
                      label="Bagages en soute inclus"
                    />
                  )}
                  name="withLuggage"
                />

                <Button
                  className="bg-brand-600 hover:bg-brand-800 w-full justify-center text-white sm:w-fit"
                  disabled={isLoading}
                  type="submit"
                >
                  <Search className="size-4" />
                  Rechercher
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </form>
  )
}

function formValuesToStructured(
  value: FlightSearchSchemaType
): StructuredMultiSearchRequest {
  return {
    dateRanges: value.flight.map((leg) => ({
      from: leg.date.from ? format(leg.date.from, 'yyyy-MM-dd') : '',
      to: leg.date.to ? format(leg.date.to, 'yyyy-MM-dd') : (leg.date.from ? format(leg.date.from, 'yyyy-MM-dd') : ''),
    })),
    flexibility: 0,
    flights: value.flight.map((leg) => ({
      departure: {
        category: leg.departure.category as 'airport' | 'city',
        iataCode: leg.departure.value,
        label: leg.departure.label,
      },
      destination: {
        category: leg.destination.category as 'airport' | 'city',
        iataCode: leg.destination.value,
        label: leg.destination.label,
      },
    })),
    passengers: value.passengers,
    travelClass: value.travelClass,
    // Défaut `true`, aligné sur le reste du chemin chatbot (`draft.luggage ?? true`).
    withLuggage: value.withLuggage ?? true,
  }
}

function parseDate(iso: string): Date {
  return parse(iso, 'yyyy-MM-dd', new Date())
}

function structuredToFormValues(params: StructuredMultiSearchRequest): FlightSearchSchemaType {
  return {
    flight: params.flights.map((f, i) => {
      const range = params.dateRanges[i]
      return {
        date: {
          from: range ? parseDate(range.from) : undefined,
          to: range ? parseDate(range.to) : undefined,
        },
        departure: {
          category: f.departure.category,
          label: f.departure.label ?? f.departure.iataCode,
          value: f.departure.iataCode,
        },
        destination: {
          category: f.destination.category,
          label: f.destination.label ?? f.destination.iataCode,
          value: f.destination.iataCode,
        },
      }
    }),
    passengers: params.passengers,
    travelClass: params.travelClass,
    type: 'multi-destination',
    withLuggage: params.withLuggage,
  }
}
